import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ParentApiService } from './parent-api-service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { InviteCodeDto } from '../entities/invite-code-dto';
import { FriendRequestDto } from '../entities/friend-request-dto';
import { SendFriendInviteDto } from '../entities/send-friend-invite-dto';
import { SendFriendRequestResponseDto } from '../entities/send-friend-request-response-dto';

@Injectable({ providedIn: 'root' })
export class FriendRequestApiService extends ParentApiService {
  private readonly baseUrl = environment.webApiAddress + '/api/friend-requests';
  private readonly CACHE_DURATION_MS = 5000; // 5 seconds

  private reloadTrigger$ = new BehaviorSubject<void>(undefined);
  private lastLoadTime: number | null = null;

  public pendingInvitesCount$: Observable<number>;

  constructor(private http: HttpClient) {
    super();

    this.pendingInvitesCount$ = this.reloadTrigger$.pipe(
      switchMap(() => this.getPendingInvites(true).pipe(
        tap(() => this.lastLoadTime = Date.now()),
        map(invites => invites?.length ?? 0),
        catchError(() => of(0))
      )),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  reloadPendingInvites(forceReload = false): void {
    if (forceReload || this.shouldReload()) {
      this.reloadTrigger$.next();
    }
  }

  getInviteCode(stayOnPageIfError?: boolean): Observable<InviteCodeDto> {
    return this.http.get<InviteCodeDto>(`${this.baseUrl}/invite-code`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getPendingInvites(stayOnPageIfError?: boolean): Observable<FriendRequestDto[]> {
    return this.http.get<FriendRequestDto[]>(`${this.baseUrl}/pending`, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  sendFriendInvite(dto: SendFriendInviteDto, stayOnPageIfError?: boolean): Observable<SendFriendRequestResponseDto> {
    return this.http.post<SendFriendRequestResponseDto>(this.baseUrl, dto, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  acceptFriendRequest(id: number, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/accept`, null, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  rejectFriendRequest(id: number, stayOnPageIfError?: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/reject`, null, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  private shouldReload(): boolean {
    if (this.lastLoadTime === null) {
      return true;
    }
    return Date.now() - this.lastLoadTime >= this.CACHE_DURATION_MS;
  }
}
